<template>
  <div class="metro-waiting-chart-container">
    <div 
      ref="container" 
      class="metro-waiting-chart"
      :class="{ 'loading': !isInViewport || !hasRendered }"
    >
      <div v-if="!isInViewport || !hasRendered" class="chart-placeholder">
        <div class="placeholder-content">
          <div class="placeholder-icon">⏱️</div>
          <div class="placeholder-text">等车时间图表加载中...</div>
        </div>
      </div>
    </div>
    <!-- 数据来源注释 -->
    <div class="data-source">
      数据来源：上海地铁官方网站
    </div>
  </div>
</template>

<script lang="ts" setup>
import * as d3 from 'd3';
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';

// 数据接口定义
interface WaitingData {
  line: string
  timeSlot: string
  section: string
  waitingTime: string
  direction: string
  waitingMinutes: number
  timeSlotOrder: number
}

interface ProcessedData extends WaitingData {
  lineColor: string
  lineNumber: number
}

const container = ref<HTMLDivElement | null>(null);
let resizeObserver: ResizeObserver | null = null;
let intersectionObserver: IntersectionObserver | null = null;
let resizeTimer: number | null = null;
const isInViewport = ref(false);
const hasRendered = ref(false);

// 地铁线路颜色配置
const lineColors: Record<string, string> = {
  '1号线': '#E3002B',
  '2号线': '#82BF25', 
  '3号线': '#FCD600',
  '4号线': '#461D84',
  '5号线': '#944D9A',
  '6号线': '#D40068',
  '7号线': '#ED6F00',
  '8号线': '#0094D8',
  '9号线': '#87CAED',
  '10号线': '#C6AFD4',
  '11号线': '#871C2B',
  '12号线': '#007A60',
  '13号线': '#E999C0',
  '14号线': '#626020',
  '15号线': '#BCA886',
  '16号线': '#98D1C0',
  '17号线': '#BC796F',
  '18号线': '#C4984F'
}

// 时段顺序映射
const timeSlotOrder: Record<string, number> = {
  '早高峰': 1,
  '平峰': 2,
  '晚高峰': 3,
  '其他时段': 4,
  '延时运营': 5
}

// 响应式数据
const waitingData = ref<WaitingData[]>([])

// 解析时间字符串为分钟数
const parseWaitingTime = (timeStr: string): number => {
  if (!timeStr) return 0
  
  // 处理范围时间（如"4分-8分"）取平均值
  if (timeStr.includes('-')) {
    const parts = timeStr.split('-')
    const minTime = parseTimeToMinutes(parts[0].trim())
    const maxTime = parseTimeToMinutes(parts[1].trim())
    return (minTime + maxTime) / 2
  }
  
  return parseTimeToMinutes(timeStr)
}

// 解析单个时间字符串为分钟数
const parseTimeToMinutes = (timeStr: string): number => {
  if (!timeStr) return 0
  
  // 移除所有空格
  const cleanStr = timeStr.replace(/\s+/g, '')
  
  // 处理"2分30秒"格式
  const minuteSecondMatch = cleanStr.match(/(\d+)分(\d+)秒/)
  if (minuteSecondMatch) {
    const minutes = parseInt(minuteSecondMatch[1])
    const seconds = parseInt(minuteSecondMatch[2])
    return minutes + seconds / 60
  }
  
  // 处理"5分"格式
  const minuteMatch = cleanStr.match(/(\d+)分/)
  if (minuteMatch) {
    return parseInt(minuteMatch[1])
  }
  
  // 处理纯数字
  const numberMatch = cleanStr.match(/(\d+)/)
  if (numberMatch) {
    return parseInt(numberMatch[1])
  }
  
  return 0
}

// 解析CSV数据
const parseCSVData = async () => {
  try {
    const response = await fetch('/waiting.csv')
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    const csvText = await response.text()
    
    const lines = csvText.split('\n')
    const processedData: WaitingData[] = []
    
    for (let i = 1; i < lines.length; i++) { // 跳过标题行
      const line = lines[i].trim()
      if (!line) continue
      
      const fields = line.split(',')
      if (fields.length < 5) continue
      
      const lineName = fields[0].trim()
      const timeSlot = fields[1].trim()
      const section = fields[2].trim()
      const waitingTime = fields[3].trim()
      const direction = fields[4].trim()
      
      if (lineName && timeSlot && waitingTime) {
        // 提取时段类型（去掉时间范围）
        const timeSlotType = timeSlot.split(' ')[0]
        
        processedData.push({
          line: lineName,
          timeSlot: timeSlotType,
          section,
          waitingTime,
          direction,
          waitingMinutes: parseWaitingTime(waitingTime),
          timeSlotOrder: timeSlotOrder[timeSlotType] || 6
        })
      }
    }
    
    if (processedData.length > 0) {
      // 按线路号和时段排序
      waitingData.value = processedData.sort((a, b) => {
        const getLineNumber = (name: string) => {
          const match = name.match(/(\d+)号线/)
          return match ? parseInt(match[1]) : 999
        }
        
        const lineCompare = getLineNumber(a.line) - getLineNumber(b.line)
        if (lineCompare !== 0) return lineCompare
        
        return a.timeSlotOrder - b.timeSlotOrder
      })
      
    } else {
      throw new Error('没有解析到有效数据')
    }
    
  } catch (error) {
    // 提供模拟数据作为备用
    waitingData.value = [
      { line: '1号线', timeSlot: '早高峰', section: '全线', waitingTime: '2分30秒', direction: '双向', waitingMinutes: 2.5, timeSlotOrder: 1 },
      { line: '1号线', timeSlot: '平峰', section: '全线', waitingTime: '5分', direction: '双向', waitingMinutes: 5, timeSlotOrder: 2 },
      { line: '2号线', timeSlot: '早高峰', section: '全线', waitingTime: '4分', direction: '双向', waitingMinutes: 4, timeSlotOrder: 1 }
    ]
  }
}

function render() {
  if (!container.value || !waitingData.value.length || !isInViewport.value) return;
  
  // 如果已经渲染过，则不重复渲染动画
  if (hasRendered.value) return;
  hasRendered.value = true;
  
  const element = container.value;
  const width = element.clientWidth;
  const height = 700;
  const margin = { top: 80, right: 40, bottom: 120, left: 100 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  
  element.innerHTML = '';
  
  const svg = d3.select(element)
    .append('svg')
    .attr('width', width)
    .attr('height', height);
  
  const g = svg.append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);
  
  // 数据处理 - 按线路聚合，计算各时段平均等车时间
  const lineDataMap = new Map<string, { [timeSlot: string]: number[] }>()
  
  waitingData.value.forEach(d => {
    if (!lineDataMap.has(d.line)) {
      lineDataMap.set(d.line, {})
    }
    const lineData = lineDataMap.get(d.line)!
    if (!lineData[d.timeSlot]) {
      lineData[d.timeSlot] = []
    }
    lineData[d.timeSlot].push(d.waitingMinutes)
  })
  
  // 转换为绘图数据
  const processedData: ProcessedData[] = []
  const timeSlots = ['早高峰', '平峰', '晚高峰', '其他时段', '延时运营']
  
  lineDataMap.forEach((timeSlotData, lineName) => {
    timeSlots.forEach(timeSlot => {
      if (timeSlotData[timeSlot] && timeSlotData[timeSlot].length > 0) {
        const avgWaitingTime = timeSlotData[timeSlot].reduce((sum, time) => sum + time, 0) / timeSlotData[timeSlot].length
        
        const getLineNumber = (name: string) => {
          const match = name.match(/(\d+)号线/)
          return match ? parseInt(match[1]) : 999
        }
        
        processedData.push({
          line: lineName,
          timeSlot,
          section: '平均',
          waitingTime: `${avgWaitingTime.toFixed(1)}分`,
          direction: '双向',
          waitingMinutes: avgWaitingTime,
          timeSlotOrder: timeSlotOrder[timeSlot] || 6,
          lineColor: lineColors[lineName] || '#6b7280',
          lineNumber: getLineNumber(lineName)
        })
      }
    })
  })
  
  // 按线路编号排序
  processedData.sort((a, b) => a.lineNumber - b.lineNumber)
  
  // 创建比例尺
  const xScale = d3.scaleBand()
    .domain(processedData.map(d => d.line))
    .range([0, innerWidth])
    .padding(0.1)
  
  const yScale = d3.scaleLinear()
    .domain([0, d3.max(processedData, d => d.waitingMinutes) || 20])
    .range([innerHeight, 0])
    .nice()
  
  // 时段颜色映射
  const timeSlotColors = {
    '早高峰': '#dc2626',
    '平峰': '#059669',
    '晚高峰': '#d97706',
    '其他时段': '#7c3aed',
    '延时运营': '#1f2937'
  }
  
  // 绘制背景网格
  const yTicks = yScale.ticks(8)
  yTicks.forEach(tick => {
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', yScale(tick))
      .attr('y2', yScale(tick))
      .attr('stroke', '#f1f5f9')
      .attr('stroke-width', 1)
      .attr('opacity', 0.8)
  })
  
  // 按线路分组数据
  const dataByLine = d3.group(processedData, d => d.line)
  
  // 为每个线路创建分组
  const lineGroups = g.selectAll('.line-group')
    .data(Array.from(dataByLine))
    .enter()
    .append('g')
    .attr('class', 'line-group')
    .attr('transform', d => `translate(${xScale(d[0]) || 0}, 0)`)
  
  // 每个线路内的时段条形图
  const timeSlotWidth = xScale.bandwidth() / 5 // 5个时段
  
  lineGroups.each(function([lineName, lineData]) {
    const group = d3.select(this)
    
    // 按时段排序
    const sortedData = Array.from(lineData).sort((a, b) => a.timeSlotOrder - b.timeSlotOrder)
    
    // 绘制每个时段的条形
    group.selectAll('.time-slot-bar')
      .data(sortedData)
      .enter()
      .append('rect')
      .attr('class', 'time-slot-bar')
      .attr('x', (d, i) => i * timeSlotWidth)
      .attr('width', timeSlotWidth - 2)
      .attr('y', innerHeight)
      .attr('height', 0)
      .attr('fill', d => timeSlotColors[d.timeSlot as keyof typeof timeSlotColors] || '#6b7280')
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1)
      .attr('rx', 2)
      .style('cursor', 'pointer')
      .style('filter', 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))')
      .on('mouseenter', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('stroke-width', 2)
          .style('filter', 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))')
        
        showTooltip(d, this, event)
      })
      .on('mouseleave', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('stroke-width', 1)
          .style('filter', 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))')
        
        hideTooltip()
      })
      .transition()
      .duration(1000)
      .delay((d, i) => {
        const lineIndex = Array.from(dataByLine.keys()).indexOf(lineName)
        return lineIndex * 100 + i * 50
      })
      .attr('y', d => yScale(d.waitingMinutes))
      .attr('height', d => Math.max(0, innerHeight - yScale(d.waitingMinutes)))
  })
  
  // Y轴
  const yAxis = d3.axisLeft(yScale)
    .tickFormat(d => `${d}分`)
    .tickSize(-innerWidth)
    .tickPadding(15)
  
  g.append('g')
    .attr('class', 'y-axis')
    .call(yAxis)
    .selectAll('text')
    .attr('font-size', '12px')
    .attr('fill', '#6b7280')
  
  g.select('.y-axis').selectAll('line')
    .attr('stroke', '#e5e7eb')
    .attr('opacity', 0.3)
  
  g.select('.y-axis').select('.domain').remove()
  
  // X轴
  const xAxis = d3.axisBottom(xScale)
    .tickFormat(d => d)
    .tickSizeOuter(0)
  
  const xAxisGroup = g.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0, ${innerHeight})`)
    .call(xAxis)
  
  // X轴标签样式 - 使用对应线路颜色
  xAxisGroup.selectAll('text')
    .attr('font-size', '12px')
    .attr('font-weight', '600')
    .attr('fill', d => lineColors[d as string] || '#374151')
    .attr('transform', 'rotate(-45)')
    .style('text-anchor', 'end')
    .attr('dx', '-0.5em')
    .attr('dy', '0.15em')
  
  // X轴线条样式
  xAxisGroup.select('.domain')
    .attr('stroke', '#e5e7eb')
    .attr('stroke-width', 1)
  
  xAxisGroup.selectAll('.tick line')
    .attr('stroke', '#e5e7eb')
    .attr('opacity', 0.5)
  
  // Y轴标题
  g.append('text')
    .attr('transform', 'rotate(-90)')
    .attr('y', -70)
    .attr('x', -innerHeight / 2)
    .attr('text-anchor', 'middle')
    .attr('font-size', '14px')
    .attr('font-weight', '600')
    .attr('fill', '#374151')
    .text('平均等车时间 (分钟)')
  
  // 图例
  const legend = g.append('g')
    .attr('transform', `translate(${innerWidth - 80}, -10)`)
  
  const legendItems = Object.entries(timeSlotColors).map(([timeSlot, color], index) => ({
    timeSlot,
    color,
    index
  }))
  
  const legendItem = legend.selectAll('.legend-item')
    .data(legendItems)
    .enter()
    .append('g')
    .attr('class', 'legend-item')
    .attr('transform', (d, i) => `translate(0, ${i * 25})`)
  
  legendItem.append('rect')
    .attr('width', 16)
    .attr('height', 16)
    .attr('rx', 2)
    .attr('fill', d => d.color)
    .attr('stroke', '#ffffff')
    .attr('stroke-width', 2)
    .style('filter', 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))')
  
  legendItem.append('text')
    .attr('x', 22)
    .attr('y', 8)
    .attr('dominant-baseline', 'middle')
    .attr('font-size', '12px')
    .attr('font-weight', '500')
    .attr('fill', '#374151')
    .text(d => d.timeSlot)
  
  // 图表标题 - 考虑图例占用空间，向左偏移
  g.append('text')
    .attr('x', (innerWidth - 100) / 2)
    .attr('y', -50)
    .attr('text-anchor', 'middle')
    .attr('font-size', '16px')
    .attr('font-weight', '700')
    .attr('fill', '#1f2937')
    .text('上海地铁各线路不同时段平均等车时间')
  
  // 副标题
  g.append('text')
    .attr('x', (innerWidth - 100) / 2)
    .attr('y', -30)
    .attr('text-anchor', 'middle')
    .attr('font-size', '12px')
    .attr('fill', '#6b7280')
    .text('数据基于各线路不同时段的发车间隔')
  
  // 提示框函数
  function showTooltip(data: ProcessedData, element: SVGRectElement, event: MouseEvent) {
    const tooltip = d3.select('body').selectAll('.waiting-tooltip').data([0])
    
    const tooltipEnter = tooltip.enter()
      .append('div')
      .attr('class', 'waiting-tooltip')
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('padding', '12px 16px')
      .style('border-radius', '8px')
      .style('font-size', '13px')
      .style('pointer-events', 'none')
      .style('z-index', '1000')
      .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.3)')
      .style('backdrop-filter', 'blur(10px)')
      .style('opacity', 0)
    
    const tooltipUpdate = tooltipEnter.merge(tooltip as any)
    
    tooltipUpdate
      .html(`
        <div style="font-weight: bold; color: ${data.lineColor}; margin-bottom: 8px;">
          ${data.line}
        </div>
        <div style="margin-bottom: 4px;">
          <strong>时段：</strong>${data.timeSlot}
        </div>
        <div>
          <strong>平均等车时间：</strong>${data.waitingMinutes.toFixed(1)}分钟
        </div>
      `)
      .style('left', (event.pageX + 10) + 'px')
      .style('top', (event.pageY - 10) + 'px')
      .transition()
      .duration(200)
      .style('opacity', 1)
  }
  
  function hideTooltip() {
    d3.select('body').selectAll('.waiting-tooltip')
      .transition()
      .duration(200)
      .style('opacity', 0)
      .remove()
  }
}

// 生命周期钩子
onMounted(async () => {
  await parseCSVData()
  
  // 设置视口观察器
  if (container.value) {
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasRendered.value) {
            isInViewport.value = true;
            render();
          }
        });
      },
      {
        threshold: 0.3, // 当30%的元素进入视口时触发
        rootMargin: '50px' // 提前50px触发
      }
    );
    
    intersectionObserver.observe(container.value);
  }
  
  // 响应式调整
  resizeObserver = new ResizeObserver(() => {
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = window.setTimeout(() => {
      if (hasRendered.value) {
        // 如果已经渲染过，允许重新渲染以适应尺寸变化
        const wasRendered = hasRendered.value;
        hasRendered.value = false;
        render();
        hasRendered.value = wasRendered;
      }
    }, 300)
  })
  
  if (container.value) {
    resizeObserver.observe(container.value)
  }
})

// 监听数据变化
watch(waitingData, () => {
  if (isInViewport.value) {
    hasRendered.value = false;
    render();
  }
}, { deep: true })

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  if (intersectionObserver) {
    intersectionObserver.disconnect()
  }
  if (resizeTimer) {
    clearTimeout(resizeTimer)
  }
})
</script>

<style scoped>
.metro-waiting-chart-container {
  position: relative;
  width: 100%;
  height: 700px;
}

/* 数据来源注释 */
.data-source {
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 12px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(100, 116, 139, 0.2);
  backdrop-filter: blur(4px);
  z-index: 10;
  font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  letter-spacing: 0.2px;
}

.metro-waiting-chart {
  width: 100%;
  height: 700px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  /* 确保字体使用思源黑体 */
  font-family: 'Noto Sans SC', 'Source Han Sans SC', 'PingFang SC', 'Microsoft YaHei', '微软雅黑', sans-serif !important;
}

.metro-waiting-chart.loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.placeholder-content {
  text-align: center;
  color: #6b7280;
}

.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.7;
  animation: pulse 2s infinite;
}

.placeholder-text {
  font-size: 1.1rem;
  font-weight: 500;
  opacity: 0.8;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .metro-waiting-chart-container {
    height: 600px;
  }
  
  .metro-waiting-chart {
    height: 600px;
  }
}

@media (max-width: 480px) {
  .metro-waiting-chart-container {
    height: 500px;
  }
  
  .metro-waiting-chart {
    height: 500px;
  }
}
</style>